import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import useRoutes from '@/hooks/useRoutes';
import { Academy } from '@/types';
import { AlertCircle, Info } from 'lucide-react';

interface CreateProps {
    academies: Academy[];
    allowed_domains: string[];
    errors?: Record<string, string>;
    form_data?: {
        email?: string;
        first_name?: string;
        last_name?: string;
        academy_id?: number;
    };
}

const CreateContent: React.FC<CreateProps> = ({
    academies,
    allowed_domains,
    errors: serverErrors,
    form_data,
}) => {
    const { route } = useRoutes();

    // Parse email if provided
    const emailParts = form_data?.email?.split('@') || ['', ''];
    const initialUsername = emailParts[0] || '';
    const initialDomain = emailParts[1] || allowed_domains[0] || '';

    const [data, setDataState] = useState({
        email_username: initialUsername,
        email_domain: initialDomain,
        first_name: form_data?.first_name || '',
        last_name: form_data?.last_name || '',
        academy_id: form_data?.academy_id || '',
    });
    const [processing, setProcessing] = useState(false);

    const setData = (key: string, value: any) => {
        setDataState((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setProcessing(true);

        router.post(
            route('staff-commercial-users-create').toString(),
            {
                email: `${data.email_username}@${data.email_domain}`,
                first_name: data.first_name,
                last_name: data.last_name,
                academy_id: data.academy_id,
            },
            {
                preserveScroll: true,
                onFinish: () => setProcessing(false),
            }
        );
    };

    const allErrors = serverErrors || {};

    return (
        <StaffLayout>
            <div className="mx-auto max-w-3xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Crear Usuario Comercial
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Completa el formulario para crear un nuevo usuario
                        comercial
                    </p>
                </div>

                {/* Info Box */}
                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="flex">
                        <Info
                            size={20}
                            className="mr-3 mt-0.5 flex-shrink-0 text-blue-600"
                        />
                        <div className="text-sm text-blue-800">
                            <p className="font-semibold">
                                Al crear este usuario:
                            </p>
                            <ul className="ml-4 mt-2 list-disc space-y-1">
                                <li>
                                    Se generará automáticamente una contraseña
                                    segura
                                </li>
                                <li>El usuario será añadido a los grupos "Comercial" y "Estudiante"</li>
                                <li>
                                    Se inscribirá automáticamente en todos los
                                    cursos de la academia seleccionada
                                </li>
                                <li>
                                    Se enviará un correo con las credenciales de
                                    acceso
                                </li>
                                <li>
                                    Deberá cambiar su contraseña en el primer
                                    inicio de sesión
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="rounded-lg bg-white dark:bg-neutral-800 p-6 shadow">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Academy - First Field */}
                        <div>
                            <label
                                htmlFor="academy_id"
                                className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
                            >
                                Academia
                            </label>
                            <select
                                id="academy_id"
                                value={data.academy_id}
                                onChange={(e) =>
                                    setData('academy_id', Number(e.target.value))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                    allErrors.academy_id
                                        ? 'border-red-300'
                                        : 'border-gray-300 dark:border-neutral-600'
                                } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                            >
                                <option value="">Selecciona una academia</option>
                                {academies.map((academy) => (
                                    <option key={academy.id} value={academy.id}>
                                        {academy.name}
                                    </option>
                                ))}
                            </select>
                            {allErrors.academy_id && (
                                <div className="mt-2 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {allErrors.academy_id}
                                </div>
                            )}
                        </div>

                        {/* Email - Split into username and domain */}
                        <div>
                            <label
                                htmlFor="email_username"
                                className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
                            >
                                Email
                            </label>
                            <div className="mt-1 flex gap-2">
                                <input
                                    type="text"
                                    id="email_username"
                                    value={data.email_username}
                                    onChange={(e) =>
                                        setData('email_username', e.target.value)
                                    }
                                    className={`block w-full rounded-md border ${
                                        allErrors.email
                                            ? 'border-red-300'
                                            : 'border-gray-300 dark:border-neutral-600'
                                    } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                                    placeholder="usuario"
                                />
                                <span className="flex items-center text-gray-500 dark:text-neutral-400">@</span>
                                <select
                                    id="email_domain"
                                    value={data.email_domain}
                                    onChange={(e) =>
                                        setData('email_domain', e.target.value)
                                    }
                                    className={`block w-full rounded-md border ${
                                        allErrors.email
                                            ? 'border-red-300'
                                            : 'border-gray-300 dark:border-neutral-600'
                                    } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                                >
                                    {allowed_domains.map((domain) => (
                                        <option key={domain} value={domain}>
                                            {domain}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {allErrors.email && (
                                <div className="mt-2 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {allErrors.email}
                                </div>
                            )}
                            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                                Dominios permitidos:{' '}
                                {allowed_domains.join(', ')}
                            </p>
                        </div>

                        {/* First Name */}
                        <div>
                            <label
                                htmlFor="first_name"
                                className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
                            >
                                Nombre
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                value={data.first_name}
                                onChange={(e) =>
                                    setData('first_name', e.target.value)
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                    allErrors.first_name
                                        ? 'border-red-300'
                                        : 'border-gray-300 dark:border-neutral-600'
                                } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                            />
                            {allErrors.first_name && (
                                <div className="mt-2 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {allErrors.first_name}
                                </div>
                            )}
                        </div>

                        {/* Last Name */}
                        <div>
                            <label
                                htmlFor="last_name"
                                className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
                            >
                                Apellido
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                value={data.last_name}
                                onChange={(e) =>
                                    setData('last_name', e.target.value)
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                    allErrors.last_name
                                        ? 'border-red-300'
                                        : 'border-gray-300 dark:border-neutral-600'
                                } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                            />
                            {allErrors.last_name && (
                                <div className="mt-2 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {allErrors.last_name}
                                </div>
                            )}
                        </div>

                        {/* General Error */}
                        {allErrors.general && (
                            <div className="rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <AlertCircle
                                        size={20}
                                        className="mr-3 text-red-600"
                                    />
                                    <div className="text-sm text-red-700">
                                        {allErrors.general}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end space-x-3 pt-4">
                            <Link href={route('staff-commercial-users').toString()}>
                                <SecondaryButton type="button">
                                    Cancelar
                                </SecondaryButton>
                            </Link>
                            <PrimaryButton type="submit" disabled={processing}>
                                {processing ? 'Creando...' : 'Crear Usuario'}
                            </PrimaryButton>
                        </div>
                    </form>
                </div>
            </div>
        </StaffLayout>
    );
};

const Create: React.FC<CreateProps> = (props) => {
    return (
        <ThemeWrapper>
            <CreateContent {...props} />
        </ThemeWrapper>
    );
};

export default Create;
